import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Tabs from "antd/es/tabs";
import { uniq } from "lodash-es";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { SimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { TeamPicker, UserPicker } from "@mapmycustomers/ui";

import { StandardFilterButton } from "@app/component/filtering";
import useView from "@app/component/view/utils/useView";
import { isCurrentUserOwner } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

import { getSchedulerResources } from "../../store";

import styles from "./Sidebar.module.scss";

interface Props {
  selectedUsers: number;
  teams: Team[];
  totalUsers: number;
  users: User[];
}

const Sidebar: React.FC<Props> = ({ selectedUsers, teams, totalUsers, users }) => {
  const intl = useIntl();

  const { updateFilterModel, viewState } = useView<Activity>();

  const selectedUserIds: User["id"][] = useMemo(
    () => (viewState.filter.assignee as SimpleCondition | undefined)?.value ?? [],
    [viewState.filter.assignee]
  );

  const handleChangeAssigneeList = useCallback(
    (userIds: Array<User["id"]>) => {
      updateFilterModel({
        assignee: { operator: FilterOperator.IN_ANY, value: userIds },
      });
    },
    [updateFilterModel]
  );

  const selectedTeams = useMemo(
    () => teams.filter((team) => team.users.every(({ id }) => selectedUserIds.includes(id))),
    [selectedUserIds, teams]
  );

  const handleChangeTeamList = useCallback(
    (updatedTeams: Team[]) => {
      const allTeamsUsersIds = new Set(teams.flatMap(({ users }) => users.map(({ id }) => id)));
      const selectedNonTeamUserIds = users
        .filter(({ id }) => selectedUserIds.includes(id) && !allTeamsUsersIds.has(id))
        .map(({ id }) => id);
      updateFilterModel({
        assignee: {
          operator: FilterOperator.IN_ANY,
          value: uniq([
            ...selectedNonTeamUserIds,
            ...updatedTeams.flatMap(({ users }) => users.map(({ id }) => id)),
          ]),
        },
      });
    },
    [selectedUserIds, teams, updateFilterModel, users]
  );

  const hiddenFilterFields = useMemo(
    () =>
      [
        activityFieldModel.getByName(ActivityFieldName.START_AT),
        activityFieldModel.getByName(ActivityFieldName.COMPLETED),
        ...activityFieldModel.fields.filter((field) =>
          field.hasFeature(FieldFeature.NON_LIST_VIEW)
        ),
      ].filter(isDefined),
    []
  );

  const hiddenSavedFilterFields = useMemo(
    () => [activityFieldModel.getByName(ActivityFieldName.START_AT)].filter(isDefined),
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.options}>
            {intl.formatMessage({
              id: "activities.schedulerView.sidebar.title",
              defaultMessage: "Options",
              description: "Timeline view sidebar title",
            })}
          </span>
          <span>
            {intl.formatMessage(
              {
                id: "activities.schedulerView.assigeesSelected",
                defaultMessage: "{count}/{total} assignees selected",
                description: "Timeline view assignees selected",
              },
              { count: selectedUsers, total: totalUsers }
            )}
          </span>
        </div>
        <div>
          <StandardFilterButton
            context="activityPage"
            entityType={EntityType.ACTIVITY}
            hiddenFields={hiddenFilterFields}
            implicitFilterFields={hiddenSavedFilterFields}
            zIndex={100} // to show filter popover above sidebar
          />
        </div>
      </div>
      <Tabs className={styles.tabs}>
        <Tabs.TabPane
          key="users"
          tab={intl.formatMessage({
            id: "activities.schedulerView.filter.tab.users.title",
            defaultMessage: "Users",
            description: "Users tab title",
          })}
        >
          <UserPicker
            onChange={handleChangeAssigneeList}
            showAvatar={false}
            users={users}
            value={selectedUserIds}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          key="teams"
          tab={intl.formatMessage({
            id: "activities.schedulerView.filter.tab.teams.title",
            defaultMessage: "Teams",
            description: "Teams tab title",
          })}
        >
          <TeamPicker onChange={handleChangeTeamList} teams={teams} value={selectedTeams} />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const users = getUsers(state).filter((user) =>
    !isCurrentUserOwner(state) ? !isOwner(user) : true
  );
  const totalUsers = users.length;
  return {
    selectedUsers: getSchedulerResources(state).length,
    teams: getTeams(state),
    totalUsers,
    users,
  };
};

export default connect(mapStateToProps)(Sidebar);
